import { Suspense } from 'react';
import { pageRegistry, PageModels } from './pages/registry';
import { getAppConfig } from '@/core/config/get-app-config';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@/core/components';

// Generic over the page key so each page's model type stays tied to its own component.
// Indexing the registry with a union key instead would collapse the models into an
// intersection, which no single page's initial data satisfies.
function renderPage<K extends keyof PageModels>(pageKey: K, initialData: PageModels[K]) {
	const { component: PageComponent, getModel } = pageRegistry[pageKey];
	const model = getModel(initialData);

	return <PageComponent model={model} />;
}

function App() {
	const { t } = useTranslation();
	const config = getAppConfig();
	const pageToLoad = config?.page_to_load;

	// Type guard: ensure it's a known page key
	if (!pageToLoad || !(pageToLoad in pageRegistry)) {
		return <div>{t('common.noPageToLoad')}</div>;
	}

	// Use assertion to help TypeScript narrow the key
	const pageKey = pageToLoad as keyof typeof pageRegistry;

	// Narrow the model type correctly
	const initialData = window.__INITIAL_DATA__ as PageModels[typeof pageKey];

	return <Suspense fallback={<Spinner />}>{renderPage(pageKey, initialData)}</Suspense>;
}

export default App;
